import * as React from 'react';
import { Row, Col } from 'reactstrap';
import { AccentDialog } from '../../dialogs/AccentDialog';
import AccentIcons from '../../icons/Icons';
import { showDialog, t } from '../../services/HelperService';
import { AccentButton } from '../AccentButton';
import { ConfiguratorContext } from './ConfigContextProvider';
import { onSizeChanged } from './ProductConfiguratorShared';
import { AccentLengthUnbound } from '../unbound/AccentLengthUnbound';

export const ConfigSizeAction = props => {
    const key = `ConfigSizeAction_${props.width}`;
    const [_, setSizeID] = React.useState(0);
    const ctx = React.useContext(ConfiguratorContext);

    const baseUnit = ctx.config.Data.BaseUnit;
    const precision = ctx.config.Data.ProductPrecision;
    const productSize = ctx.config.Data.GetSize();
    const orderLine = ctx.config.OrderLine;

    const fireChange = React.useMemo(() => onSizeChanged(), [ctx.productID]);

    const viewModel = React.useMemo(
        () => new window.InsyteProduct.Models.MultiSizeViewModel(
            orderLine.Width,
            orderLine.Height,
            orderLine.WidthMeasurements,
            orderLine.HeightMeasurements,
            productSize
        ),
        [orderLine.Width, orderLine.Height, orderLine.WidthMeasurements, orderLine.HeightMeasurements, productSize]
    );

    const onSizeChange = React.useCallback(e => {
        setSizeID(s => s + 1);
    }, [setSizeID]);

    const onClick = React.useCallback(e => {
        showDialog(<SelectSizeDlg
            unit={baseUnit}
            precision={precision}

            // Pass width values
            width1Value={viewModel.Width1Value}
            width2Value={viewModel.Width2Value}
            width3Value={viewModel.Width3Value}

            // Pass height values
            height1Value={viewModel.Height1Value}
            height2Value={viewModel.Height2Value}
            height3Value={viewModel.Height3Value}

            // Pass visibility flags
            width1Visible={viewModel.Width1Visible}
            width2Visible={viewModel.Width2Visible}
            width3Visible={viewModel.Width3Visible}
            height1Visible={viewModel.Height1Visible}
            height2Visible={viewModel.Height2Visible}
            height3Visible={viewModel.Height3Visible}

            // Pass labels
            labelWidth1={viewModel.LabelWidth1}
            labelWidth2={viewModel.LabelWidth2}
            labelWidth3={viewModel.LabelWidth3}
            labelHeight1={viewModel.LabelHeight1}
            labelHeight2={viewModel.LabelHeight2}
            labelHeight3={viewModel.LabelHeight3}
        />).then(res => {
            if (!res.canceled) {
                viewModel.Width1Value = res.width1Value;
                viewModel.Width2Value = res.width2Value;
                viewModel.Width3Value = res.width3Value;
                viewModel.Height1Value = res.height1Value;
                viewModel.Height2Value = res.height2Value;
                viewModel.Height3Value = res.height3Value;

                orderLine.Width = viewModel.CalculatedWidth.RoundTo(precision);
                orderLine.Height = viewModel.CalculatedHeight.RoundTo(precision);
                orderLine.WidthMeasurements = viewModel.WidthMeasurements;
                orderLine.HeightMeasurements = viewModel.HeightMeasurements;

                fireChange(ctx);
            }
        });
    }, [viewModel, orderLine, fireChange, ctx, baseUnit, precision]);

    React.useEffect(() => {
        ctx.config.Subscribe(window.InsyteConfig.InsyteConfigEvents.SizeChangeOccurred, onSizeChange, key);
        return () => {
            ctx.config.UnSubscribe(window.InsyteConfig.InsyteConfigEvents.SizeChangeOccurred, key);
        };
    }, [ctx, key]);

    return <AccentIcons.More onClick={onClick} />;
};

export const SelectSizeDlg = props => {
    const [width1Value, setWidth1Value] = React.useState(props.width1Value);
    const [width2Value, setWidth2Value] = React.useState(props.width2Value);
    const [width3Value, setWidth3Value] = React.useState(props.width3Value);

    const [height1Value, setHeight1Value] = React.useState(props.height1Value);
    const [height2Value, setHeight2Value] = React.useState(props.height2Value);
    const [height3Value, setHeight3Value] = React.useState(props.height3Value);

    const onClose = React.useCallback(e => {
        props.onClose({
            width1Value,
            width2Value,
            width3Value,
            height1Value,
            height2Value,
            height3Value
        });
    }, [props, width1Value, width2Value, width3Value, height1Value, height2Value, height3Value]);

    return (
        <AccentDialog
            onClose={props.onClose}
            headerContent={"Size Details"}
            actionsContent={[
                <AccentButton key="2" tagName="sizeCloseBtn" onClick={onClose}>
                    {t("application_strings.application.buttons.ok")}
                </AccentButton>
            ]}
        >
            <Row>
                <Col md={12}>
                    <Row>
                        <Col md={3}></Col>
                        <Col md={3}>Width</Col>
                        <Col md={3}></Col>
                        <Col md={3}>Height</Col>
                    </Row>
                    <Row>
                        <Col md={3}>{props.width1Visible && props.labelWidth1}</Col>
                        <Col md={3}>
                            {props.width1Visible && (
                                <AccentLengthUnbound
                                    defaultUnit={props.unit}
                                    precision={props.precision}
                                    onChange={setWidth1Value}
                                    min={0}
                                    defaultValue={props.width1Value}
                                />
                            )}
                        </Col>
                        <Col md={3}>{props.height1Visible && props.labelHeight1}</Col>
                        <Col md={3}>
                            {props.height1Visible && (
                                <AccentLengthUnbound
                                    defaultUnit={props.unit}
                                    precision={props.precision}
                                    onChange={setHeight1Value}
                                    min={0}
                                    defaultValue={props.height1Value}
                                />
                            )}
                        </Col>
                    </Row>
                    <Row>
                        <Col md={3}>{props.width2Visible && props.labelWidth2}</Col>
                        <Col md={3}>
                            {props.width2Visible && (
                                <AccentLengthUnbound
                                    defaultUnit={props.unit}
                                    precision={props.precision}
                                    onChange={setWidth2Value}
                                    min={0}
                                    defaultValue={props.width2Value}
                                />
                            )}
                        </Col>
                        <Col md={3}>{props.height2Visible && props.labelHeight2}</Col>
                        <Col md={3}>
                            {props.height2Visible && (
                                <AccentLengthUnbound
                                    defaultUnit={props.unit}
                                    precision={props.precision}
                                    onChange={setHeight2Value}
                                    min={0}
                                    defaultValue={props.height2Value}
                                />
                            )}
                        </Col>
                    </Row>
                    <Row>
                        <Col md={3}>{props.width3Visible && props.labelWidth3}</Col>
                        <Col md={3}>
                            {props.width3Visible && (
                                <AccentLengthUnbound
                                    defaultUnit={props.unit}
                                    precision={props.precision}
                                    onChange={setWidth3Value}
                                    min={0}
                                    defaultValue={props.width3Value}
                                />
                            )}
                        </Col>
                        <Col md={3}>{props.height3Visible && props.labelHeight3}</Col>
                        <Col md={3}>
                            {props.height3Visible && (
                                <AccentLengthUnbound
                                    defaultUnit={props.unit}
                                    precision={props.precision}
                                    onChange={setHeight3Value}
                                    min={0}
                                    defaultValue={props.height3Value}
                                />
                            )}
                        </Col>
                    </Row>
                </Col>
            </Row>
        </AccentDialog>
    );
};